<template>
    <div class="ps">
        <ProductImageViewerCmp
            :images="viewer.images"
            :start-index="viewer.index"
            :visible="viewer.visible"
            @close="viewer.visible = false" />

        <header class="ps-hero" data-tour="product-hero">
            <button type="button" class="ps-hero-thumb"
                :class="{'is-empty': !heroImages.length, 'is-deck': heroImages.length > 1}"
                :disabled="!heroImages.length"
                :aria-label="heroImages.length > 1 ? `Ver as ${heroImages.length} imagens do produto` : 'Ver imagem do produto'"
                @click="openImageViewer(heroImages, 0)">
                <span v-for="(img, i) in heroDeck" :key="`${i}-${img.url}`"
                    class="ps-deck-card" :class="[`is-depth-${i}`, {'is-inactive': !img.isActive}]">
                    <img :src="img.url" :alt="i === 0 ? 'Imagem principal do produto' : ''" />
                </span>
                <fa v-if="!heroImages.length" icon="images" />
                <span v-if="heroImages.length > 1" class="ps-deck-count">+{{heroImages.length - 1}}</span>
            </button>

            <div class="ps-hero-main">
                <p class="ps-eyebrow">Produto com grade</p>
                <h2 class="ps-hero-title">{{product.name}}</h2>

                <div class="ps-hero-chips">
                    <span class="ps-status" :class="product.isActive ? 'is-active' : 'is-inactive'">
                        <span class="ps-status-dot"></span>
                        {{product.isActive ? 'Ativo' : 'Inativo'}}
                    </span>
                    <span class="ps-chip"><small>Grade</small><code>{{gridAxes}}</code></span>
                    <span class="ps-chip">
                        <small>Cód. retaguarda</small><code>{{product.codeSource | infoOrPlaceholder}}</code>
                        <CopyButtonCmp v-if="product.codeSource" class="ps-chip-copy" :value="product.codeSource" label="código da retaguarda" />
                    </span>
                </div>
            </div>

            <dl class="ps-hero-figures">
                <div>
                    <dt>Variações</dt>
                    <dd>{{productVariations.length}}</dd>
                </div>
                <div>
                    <dt>Em estoque</dt>
                    <dd>{{totalStockQty}}</dd>
                </div>
            </dl>
        </header>

        <div class="ps-grid">
            <div class="ps-col">
                <section class="ps-section ps-desc" :class="{'is-collapsed': !descriptionOpen}">
                    <div class="ps-section-head ps-desc-head">
                        <button type="button" class="ps-desc-toggle"
                            :aria-expanded="String(descriptionOpen)" aria-controls="ps-description-panel"
                            :title="descriptionOpen ? 'Recolher descrição' : 'Mostrar descrição'"
                            @click="toggleDescription">
                            <fa icon="chevron-down" class="ps-desc-chevron" />
                            <span class="ps-section-title">
                                Descrição<template v-if="descriptionOpen"> {{currentDescription.label.toLowerCase()}}</template>
                            </span>

                            <template v-if="!descriptionOpen">
                                <span class="ps-desc-pills">
                                    <span v-for="tab in descriptionTabs" :key="tab.id"
                                        class="ps-desc-pill" :class="{'has-text': tab.has}"
                                        :title="`Descrição ${tab.label.toLowerCase()} ${tab.has ? 'cadastrada' : 'não cadastrada'}`">
                                        {{tab.label}}
                                    </span>
                                </span>
                                <span class="ps-desc-preview" :class="{'is-empty': !collapsedPreview}">
                                    {{collapsedPreview || 'Nenhuma descrição cadastrada'}}
                                </span>
                            </template>
                        </button>

                        <div v-if="descriptionOpen" class="ps-tabs" role="tablist" aria-label="Tipo de descrição">
                            <button v-for="tab in descriptionTabs" :key="tab.id" type="button" role="tab"
                                class="ps-tab" :class="{'is-active': descriptionTab === tab.id, 'is-empty': !tab.has}"
                                :aria-selected="String(descriptionTab === tab.id)"
                                @click="descriptionTab = tab.id">
                                {{tab.label}}
                            </button>
                        </div>
                    </div>

                    <div id="ps-description-panel" class="ps-collapse" :class="{'is-open': descriptionOpen}"
                        :aria-hidden="String(!descriptionOpen)">
                        <div class="ps-collapse-inner">
                            <div class="ps-description" role="tabpanel">
                                <p v-if="currentDescription.has" v-html="currentDescription.html"></p>
                                <p v-else class="ps-empty">Descrição {{currentDescription.label.toLowerCase()}} não cadastrada.</p>
                            </div>
                        </div>
                    </div>
                </section>

                <section class="ps-channels" data-tour="product-channels" v-if="product.hasStores() && parentChannels.length">
                    <div class="ps-channels-head">
                        <h3 class="ps-section-title">Canais</h3>
                        <span class="ps-count">{{parentChannels.length}}</span>
                    </div>
                    <p class="ps-section-description">
                        Cada canal confere o produto pai. Os IDs de cada variação ficam em Variações.
                    </p>

                    <ProductChannelCardListCmp
                        class="ps-channel-list"
                        grade-mode
                        :store-channels="parentChannels"
                        :channel-list="channelList"
                        :id-store="parentStoreId"
                        :id-muven="product.id"
                        :show-stock="false"
                        :grid-variations-of="gridCoverageOf"
                        id-label="ID do pai no canal"
                        @checked="onParentChecked"
                        @associate="(storeChannel, current) => openGridAssociation(storeChannel, current)" />
                </section>

                <section class="ps-section pv" data-tour="product-variations">
                    <div class="ps-section-head">
                        <h3 class="ps-section-title">Variações</h3>
                        <span class="ps-count">{{productVariations.length}}</span>
                    </div>

                    <div class="pv-list">
                        <article class="pv-item" v-for="gridRow in productVariations" :key="gridRow.id"
                            :class="{'is-open': !gridRow.collapsed}">
                            <button type="button" class="pv-row" :aria-expanded="String(!gridRow.collapsed)"
                                @click="toggleVariationCollapse(gridRow)">
                                <span class="pv-values">
                                    <span class="pv-value" :title="bidimensionalGrid.getFirstName()">{{gridRow.firstVariation}}</span>
                                    <span class="pv-value" v-if="!!bidimensionalGrid.getSecondName()" :title="bidimensionalGrid.getSecondName()">{{gridRow.secondVariation}}</span>
                                </span>

                                <span class="pv-codes">
                                    <span><small>SKU</small><code>{{gridRow.skuCode | infoOrPlaceholder}}</code></span>
                                    <span><small>Retaguarda</small><code>{{gridRow.codeSource | infoOrPlaceholder}}</code></span>
                                </span>

                                <span class="pv-stock">
                                    <strong>{{gridRow.stockQty}}</strong>
                                    <small>em estoque</small>
                                </span>

                                <span class="ps-status" :class="gridRow.isActive ? 'is-active' : 'is-inactive'">
                                    <span class="ps-status-dot"></span>
                                    {{gridRow.isActive ? 'Ativo' : 'Inativo'}}
                                </span>

                                <fa icon="chevron-down" class="pv-chevron" />
                            </button>

                            <div class="pv-panel" v-if="!gridRow.collapsed">
                                <p v-if="describeStockConversion(gridRow.stockConversionRatio).hasConversion" class="pv-conversion">
                                    <span class="ps-conversion"><fa icon="info-circle" /> {{describeStockConversion(gridRow.stockConversionRatio).label}}</span>
                                    <small class="ps-conversion-hint">{{describeStockConversion(gridRow.stockConversionRatio).hint}}</small>
                                </p>

                                <div class="ps-metrics" v-if="gridRow.stores.length === 0">
                                    <div><span>Estoque</span><strong>Desconhecido</strong></div>
                                    <div><span>Reservado</span><strong>Desconhecido</strong></div>
                                    <div><span>Disponível</span><strong>Desconhecido</strong></div>
                                </div>

                                <div class="ps-metrics" v-if="gridRow.stores.length === 1">
                                    <div>
                                        <span>Estoque</span>
                                        <strong>{{gridRow.stores[0].stockQty | infoOrPlaceholder}}</strong>
                                    </div>
                                    <div>
                                        <span>
                                            Reservado
                                            <a :href="urlToReservedStockOrder(gridRow)" 
                                                target="_blank"    
                                                v-show="!!gridRow.stores[0].reservedStockQty"
                                                title="Pedidos pendentes com esta variação">
                                                <fa icon="external-link-alt" />
                                            </a>
                                        </span>
                                        <strong>{{gridRow.stores[0].reservedStockQty | infoOrPlaceholder}}</strong>
                                    </div>
                                    <div>
                                        <span>Disponível</span>
                                        <strong>{{gridRow.stores[0].totalAvailableStockQty()}}</strong>
                                    </div>
                                </div>

                                <div class="pv-stores" v-if="gridRow.stores.length > 1">
                                    <div class="pv-stores-head">
                                        <div class="pv-store-picker">
                                            <label class="ps-label">Loja</label>
                                            <multiselect
                                                v-model="gridRow.chosenStore"
                                                :options="gridRow.storeOptions"
                                                :multiple="false"
                                                :show-labels="false"
                                                :allow-empty="false"
                                                label="name"
                                                track-by="id"
                                                @select="selectProductStore"
                                                placeholder="" />
                                            <small v-show="!!gridRow.chosenProductStore.updatedAt" class="text-muted">Última atualização em {{gridRow.chosenProductStore.updatedAt | dateTimeFilter | infoOrPlaceholder}}</small>
                                        </div>

                                        <dl class="ps-facts">
                                            <div>
                                                <dt>Estoque somado</dt>
                                                <dd>{{gridRow.stockQty}}</dd>
                                            </div>
                                        </dl>
                                    </div>

                                    <div class="muven-tip tip-danger mt-3 mb-0" v-show="gridRow.chosenProductStore.blockStock">
                                        <fa icon="info" class="tip-icon" />
                                        <p>Esta variação tem o estoque bloqueado na loja {{gridRow.chosenStore.name}}.</p>
                                    </div>

                                    <div class="ps-metrics">
                                        <div>
                                            <span>Listagem</span>
                                            <strong>{{gridRow.chosenProductStore.listPrice | currency}}</strong>
                                        </div>
                                        <div>
                                            <span>Venda</span>
                                            <strong>{{gridRow.chosenProductStore.sellPrice | currency}}</strong>
                                        </div>
                                        <div>
                                            <span>Custo</span>
                                            <strong>{{gridRow.chosenProductStore.costPrice | currency}}</strong>
                                        </div>
                                        <template v-if="!gridRow.chosenProductStore.blockStock">
                                            <div>
                                                <span>Estoque</span>
                                                <strong>{{gridRow.chosenProductStore.stockQty}}</strong>
                                            </div>
                                            <div>
                                                <span>
                                                    Reservado
                                                    <a :href="urlToReservedStockOrder(gridRow)" 
                                                        target="_blank"    
                                                        v-show="!!gridRow.chosenProductStore.reservedStockQty"
                                                        title="Pedidos pendentes com esta variação">
                                                        <fa icon="external-link-alt" />
                                                    </a>
                                                </span>
                                                <strong>{{gridRow.chosenProductStore.reservedStockQty}}</strong>
                                            </div>
                                            <div>
                                                <span>Disponível</span>
                                                <strong>{{gridRow.chosenProductStore.totalAvailableStockQty ? gridRow.chosenProductStore.totalAvailableStockQty() : '-'}}</strong>
                                            </div>
                                        </template>
                                        <div v-else class="is-span">
                                            <span>Estoque</span>
                                            <strong>Bloqueado</strong>
                                        </div>
                                    </div>
                                </div>

                                <div class="pv-block" v-if="!!gridRow.uniqueChannels.length">
                                    <p class="ps-label">Nos canais</p>

                                    <div class="pv-table-wrap">
                                        <table class="pv-table">
                                            <thead>
                                                <tr>
                                                    <th>Canal</th>
                                                    <th>
                                                        <span class="label-with-tooltip">
                                                            Loja
                                                            <InfoPopoverCmp title="De onde vem esse vínculo?">
                                                                <p>
                                                                    A relação entre uma variação e sua loja <strong>não é configurada no Muven</strong>.
                                                                    Ela é definida na sua <em>retaguarda (ERP)</em>, que envia essa informação ao Muven via integração.
                                                                </p>
                                                                <p>
                                                                    Isso é diferente das <em>lojas responsáveis pelo estoque</em>, nas configurações do canal,
                                                                    que decidem de qual loja o estoque é enviado ao canal.
                                                                </p>
                                                            </InfoPopoverCmp>
                                                        </span>
                                                    </th>
                                                    <th>Listagem / venda</th>
                                                    <th class="is-num">Estoque</th>
                                                    <th>ID no canal</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <template v-for="row in variationTableRows(gridRow)">
                                                    <tr v-if="row.type === 'group'" :key="`group-${row.key}`" class="pv-table-group">
                                                        <td colspan="5">
                                                            <InactiveChannelsToggleCmp compact
                                                                :channels="row.channels"
                                                                :open="!!showInactiveByVariation[gridRow.id]"
                                                                @toggle="toggleInactiveChannels(gridRow)" />
                                                        </td>
                                                    </tr>

                                                    <tr v-else :key="`channel-${row.key}`" :class="{'is-off': !row.isActive}">
                                                        <td>
                                                            <ChannelLogoCmp :channel="row.storeChannel.channel" :size="20"
                                                                :muted="!row.isActive"
                                                                :caption="row.isActive ? '' : 'Canal inativo'" />
                                                        </td>
                                                        <td>{{row.storeChannel.storeName | infoOrPlaceholder}}</td>
                                                        <td class="is-nowrap">{{row.storeChannel.listPrice | currency}} / {{row.storeChannel.sellPrice | currency}}</td>
                                                        <td class="is-num">{{row.storeChannel.calculatedStockQty | infoOrPlaceholder}}</td>
                                                        <td>
                                                            <span class="pv-id-cell">
                                                                <code v-if="row.storeChannel.idExternalChannel" class="pv-id">{{row.storeChannel.idExternalChannel}}</code>
                                                                <span v-else class="ps-empty">Sem associação</span>

                                                                <!-- Conferido junto com o produto pai associado no canal -->
                                                                <span v-if="variationMark(row.storeChannel) === 'ok'" class="pv-mark is-ok"
                                                                    title="Esta variação está no produto pai associado no canal">
                                                                    <fa icon="check-circle" />
                                                                    <span class="sr-only">Confere no canal</span>
                                                                </span>
                                                                <span v-else-if="variationMark(row.storeChannel) === 'bad'" class="pv-mark is-bad"
                                                                    title="Esta variação não está no produto pai associado no canal">
                                                                    <fa icon="exclamation-triangle" />
                                                                    <span class="sr-only">Não está no produto pai associado</span>
                                                                </span>

                                                                <button v-if="canAssociateGrid(row.storeChannel)" type="button" class="pv-swap"
                                                                    :title="`${row.storeChannel.idExternalChannel ? 'Trocar' : 'Associar'} esta variação no canal ${row.storeChannel.channel.name}${hasMoreThanOneStore ? `, na loja ${row.storeChannel.storeName}` : ''}`"
                                                                    @click="openGridAssociation(parentChannelFor(row.storeChannel), null, gridRow.id)">
                                                                    <template v-if="row.storeChannel.idExternalChannel"><fa icon="edit" /> Trocar</template>
                                                                    <template v-else><fa icon="link" /> Associar</template>
                                                                </button>
                                                            </span>
                                                        </td>
                                                    </tr>
                                                </template>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <dl class="ps-facts pv-block">
                                    <div>
                                        <dt>EAN</dt>
                                        <dd class="ps-copy-row"><span>{{gridRow.eanCode | infoOrPlaceholder}}</span><CopyButtonCmp v-if="gridRow.eanCode" class="ps-dd-copy" :value="gridRow.eanCode" label="EAN" /></dd>
                                    </div>
                                    <div>
                                        <dt>NCM</dt>
                                        <dd class="ps-copy-row"><span>{{gridRow.ncmCode | infoOrPlaceholder}}</span><CopyButtonCmp v-if="gridRow.ncmCode" class="ps-dd-copy" :value="gridRow.ncmCode" label="NCM" /></dd>
                                    </div>
                                    <div>
                                        <dt>Peso</dt>
                                        <dd>{{gridRow.weight | infoOrPlaceholder}} kg</dd>
                                    </div>
                                    <div>
                                        <dt>Altura</dt>
                                        <dd>{{gridRow.height | infoOrPlaceholder}} cm</dd>
                                    </div>
                                    <div>
                                        <dt>Largura</dt>
                                        <dd>{{gridRow.width | infoOrPlaceholder}} cm</dd>
                                    </div>
                                    <div>
                                        <dt>Profundidade</dt>
                                        <dd>{{gridRow.depth | infoOrPlaceholder}} cm</dd>
                                    </div>
                                    <div v-if="gridRow.stores.length === 1">
                                        <dt>Atualização por loja</dt>
                                        <dd>{{gridRow.stores[0].updatedAt | dateTimeFilter | infoOrPlaceholder}}</dd>
                                    </div>
                                </dl>

                                <div class="pv-block pv-footer">
                                    <div class="pv-images">
                                        <p class="ps-label">Imagens</p>
                                        <span v-if="!gridRow.images.length" class="ps-empty">Nenhuma imagem.</span>
                                        <div v-else class="pv-thumbs">
                                            <button v-for="(image, index) in gridRow.images" :key="index" type="button"
                                                class="pv-thumb" :title="image.getSizeInformation()"
                                                @click="openImageViewer(gridRow.images, index)">
                                                <img :src="image.url" alt="Imagem da variação">
                                            </button>
                                        </div>
                                    </div>

                                    <div class="pv-links">
                                        <a :href="getAuditUrl(gridRow.id, EntityEnum.Product)" target="_blank">
                                            Auditoria <fa icon="external-link-alt" />
                                        </a>
                                        <a :href="urlToOrder(gridRow)" target="_blank">
                                            Pedidos <fa icon="external-link-alt" />
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </article>
                    </div>

                    <div class="pv-count">
                        <span>{{productVariations.length}} {{productVariations.length === 1 ? 'variação' : 'variações'}}</span>
                        <span class="pv-count-separator">·</span>
                        <span>{{totalStockQty}} em estoque</span>
                        <InfoPopoverCmp title="Como esse total é calculado?">
                            <p>
                                É a soma do <em>estoque somado</em> de cada variação, ou seja,
                                o estoque consolidado entre todas as lojas daquela variação.
                            </p>
                        </InfoPopoverCmp>
                    </div>
                </section>

                <section class="ps-section">
                    <div class="ps-section-head">
                        <h3 class="ps-section-title">Imagens</h3>
                        <span class="ps-count" v-if="product.hasImages()">{{product.getImages().length}}</span>
                    </div>

                    <p v-if="!product.hasImages()" class="ps-empty">Nenhuma imagem vinculada ao produto pai.</p>

                    <div v-else class="ps-gallery">
                        <figure v-for="(img, index) in product.getImages()" :key="index" class="ps-image"
                            :class="{'is-main': img.isMain && img.isActive, 'is-inactive': !img.isActive}">
                            <button type="button" @click="openImageViewer(product.getImages(), index)">
                                <img :src="img.url" alt="Imagem do produto">
                            </button>
                            <figcaption>
                                <span>{{img.getSizeInformation()}}</span>
                                <span v-if="img.isMain && img.isActive" class="ps-image-badge">Principal</span>
                                <span v-if="!img.isActive" class="ps-image-badge is-muted">Inativa</span>
                            </figcaption>
                        </figure>
                    </div>
                </section>
            </div>

            <aside class="ps-col">
                <section class="ps-section" data-tour="product-backend">
                    <h3 class="ps-section-title">Retaguarda</h3>

                    <div class="pr-ids">
                        <div class="pr-id">
                            <span class="pr-id-label">Id</span>
                            <code>{{product.idSource | infoOrPlaceholder}}</code>
                            <CopyButtonCmp v-if="product.idSource" class="pr-id-copy" :value="product.idSource" label="id na retaguarda" />
                        </div>
                        <div class="pr-id">
                            <span class="pr-id-label">Código</span>
                            <code>{{product.codeSource | infoOrPlaceholder}}</code>
                            <CopyButtonCmp v-if="product.codeSource" class="pr-id-copy" :value="product.codeSource" label="código na retaguarda" />
                        </div>
                    </div>

                    <div class="pr-group">
                        <p class="pr-group-title">
                            O que vem da retaguarda
                            <InfoPopoverCmp title="O que vem da retaguarda?">
                                <p>
                                    Mostra o que a <em>retaguarda (ERP)</em> pode atualizar neste produto pela integração.
                                    O que aparece em <strong>roxo</strong> chega do ERP; o que está apagado é mantido no
                                    <strong>Muven</strong> e não é sobrescrito.
                                </p>
                                <p v-if="product.getAllowImportDescription()"><strong>Neste produto:</strong> {{product.getAllowImportDescription().toLowerCase()}}.</p>
                                <p>Para mudar, edite o produto e ajuste a <strong>importação da retaguarda</strong>.</p>
                            </InfoPopoverCmp>
                        </p>
                        <ul class="pr-scope">
                            <li v-for="item in importScope" :key="item.key" :class="{'is-included': item.included}">
                                <fa :icon="item.included ? 'check' : 'minus'" />
                                {{item.label}}
                                <span class="sr-only">{{item.included ? 'importado' : 'não importado'}}</span>
                            </li>
                        </ul>
                    </div>

                    <div class="pr-group">
                        <p class="pr-group-title">Estoque <span>· vindo do ERP, por variação</span></p>
                        <div class="pr-conversion" :class="{'has-conversion': variationsWithConversion.length > 0}">
                            <fa icon="exchange-alt" class="pr-conversion-icon" />
                            <div>
                                <strong>{{variationsWithConversion.length ? `${variationsWithConversion.length} de ${productVariations.length} com conversão` : 'Sem conversão (1:1)'}}</strong>
                                <span>{{variationsWithConversion.length ? 'Veja o fator dentro de cada variação' : '1 un. no ERP = 1 un. vendida nos canais'}}</span>
                            </div>
                            <InfoPopoverCmp class="pr-conversion-info" title="O que é a conversão de estoque?">
                                <p>
                                    É o fator que a <em>retaguarda (ERP)</em> informa para dizer quantas unidades de venda
                                    equivalem a 1 unidade do estoque de lá. O <strong>Muven</strong> só exibe esse valor. Todo o cálculo é feito pelo canal.
                                </p>
                                <p>
                                    Exemplo: o ERP controla em <strong>caixa com 12</strong> e o canal vende por unidade,
                                    então o fator é 12. Com fator <strong>1</strong>, estoque e venda usam a mesma unidade.
                                </p>
                                <p>
                                    Se o estoque no canal não bater com o do ERP, confira esse fator: a diferença de unidade
                                    costuma ser o motivo.
                                </p>
                            </InfoPopoverCmp>
                        </div>
                    </div>

                    <dl class="pr-meta">
                        <div>
                            <dt>Prazo adicional de entrega</dt>
                            <dd>{{product.additionalDeliveryDays | deliveryDays}}</dd>
                        </div>
                        <div>
                            <dt>Cadastro atualizado em</dt>
                            <dd>{{product.updatedAt | dateTimeFilter | infoOrPlaceholder}}</dd>
                        </div>
                    </dl>
                </section>

                <section class="ps-section" v-if="!product.isVirtual">
                    <h3 class="ps-section-title">Cubagem</h3>

                    <div class="ps-metrics is-two">
                        <div>
                            <span>Peso</span>
                            <strong>{{product.weight | infoOrPlaceholder}} <small>kg</small></strong>
                        </div>
                        <div>
                            <span>Altura</span>
                            <strong>{{product.height | infoOrPlaceholder}} <small>cm</small></strong>
                        </div>
                        <div>
                            <span>Largura</span>
                            <strong>{{product.width | infoOrPlaceholder}} <small>cm</small></strong>
                        </div>
                        <div>
                            <span>Profundidade</span>
                            <strong>{{product.depth | infoOrPlaceholder}} <small>cm</small></strong>
                        </div>
                    </div>
                </section>

                <section class="ps-section">
                    <h3 class="ps-section-title">Catálogo</h3>

                    <dl class="ps-facts">
                        <div>
                            <dt>Marca</dt>
                            <dd>{{product.getBrand().name | infoOrPlaceholder}}</dd>
                        </div>
                        <div class="is-wide">
                            <dt>Categorias</dt>
                            <dd>
                                <span v-if="!product.hasCategories()">-</span>
                                <span v-for="(productCategoryChannel, index) in product.getCategories()" :key="index" class="ps-tag">
                                    {{productCategoryChannel.category.name | infoOrPlaceholder}}
                                </span>
                            </dd>
                        </div>
                    </dl>
                </section>

                <section class="ps-section">
                    <h3 class="ps-section-title">SEO e vídeo</h3>

                    <dl class="ps-facts">
                        <div class="is-wide">
                            <dt>Título</dt>
                            <dd>{{product.seoTitle | infoOrPlaceholder}}</dd>
                        </div>
                        <div class="is-wide">
                            <dt>Descrição</dt>
                            <dd><ProductScrollTextCmp>{{product.seoDescription | infoOrPlaceholder}}</ProductScrollTextCmp></dd>
                        </div>
                        <div class="is-wide">
                            <dt>Palavras chave</dt>
                            <dd><ProductScrollTextCmp>{{product.seoKeywords | infoOrPlaceholder}}</ProductScrollTextCmp></dd>
                        </div>
                        <div class="is-wide">
                            <dt>URL do vídeo</dt>
                            <dd class="ps-break">{{product.videoUrl | infoOrPlaceholder}}</dd>
                        </div>
                    </dl>
                </section>
            </aside>
        </div>

        <ProductGridAssociationModalCmp ref="gridModal" />
    </div>
</template>

<script>
import { mapState } from 'vuex'
import { ProductModel } from '@/product/models/ProductModel'
import { dateTimeFilter } from '@/utils/filters/DateTimeFilter'
import { infoOrPlaceholder } from '@/utils/filters/InformationOrPlaceholderFilter'
import { EntityEnum, OrderStatusEnum } from '@c2b/muven-commons'
import GetAuditUrl from '../../utils/mixins/GetAuditUrl'
import { StoreModel } from '@/store/models/StoreModel'
import { ProductStoreModel } from '../models/ProductStoreModel'
import ProductChannelCardListCmp from './ProductChannelCardListCmp.vue'
import InactiveChannelsToggleCmp from '@/channel/components/InactiveChannelsToggleCmp.vue'
import { splitStoreChannelsByChannelState } from '@/channel/utils/ChannelListSorter'
import ChannelLogoCmp from '@/channel/components/ChannelLogoCmp.vue'
import ProductGridAssociationModalCmp from './ProductGridAssociationModalCmp.vue'
import ProductImageViewerCmp from './ProductImageViewerCmp.vue'
import CopyButtonCmp from '@/utils/components/CopyButtonCmp.vue'
import InfoPopoverCmp from '@/utils/components/InfoPopoverCmp.vue'
import ProductScrollTextCmp from './ProductScrollTextCmp.vue'
import { orderImagesForDisplay, hasActiveImage } from '../utils/ProductImageOrder'
import { descriptionPreview, readDescriptionOpen, saveDescriptionOpen } from '../utils/DescriptionPanelHelper'
import { describeStockConversion, hasStockConversion } from '../utils/StockConversionHelper'
import { getImportScope } from '../utils/AllowImportHelper'
import { deliveryDays } from '../utils/DeliveryDaysFilter'
import { getChannelSearchProfile } from '@/channel/utils/ChannelProductAssociationHelper'

export default {
    name: 'ProductWithVariationsCmp',    
    components: {
        ProductChannelCardListCmp,
        InactiveChannelsToggleCmp,
        ChannelLogoCmp,
        ProductGridAssociationModalCmp,
        ProductImageViewerCmp,
        CopyButtonCmp,
        InfoPopoverCmp,
        ProductScrollTextCmp,
    },
    props: {
        product: {
            required: true,
            type: ProductModel,
            default: new ProductModel(),
        },
        // Canais configurados do assinante: é deles que vem se o canal está ativo
        channelList: {
            type: Array,
            default: () => [],
        },
    },
    data() {
       return {
            EntityEnum,
            viewer: {images: [], index: 0, visible: false},
            descriptionTab: 'main',
            descriptionOpen: readDescriptionOpen(),
            // Canais inativos abertos na tabela de cada variação, por id da variação
            showInactiveByVariation: {},
            // Variações de cada canal que vieram na conferência do produto pai, por id do canal
            parentCheckByChannel: {},
            productVariations: this.product.toBidimensionalGrid().getGrid().map((gridRow) => {
                let storeOptions = []

                if (gridRow.stores.length) {
                    storeOptions = StoreModel.getStoreListWithIdAndName(gridRow.stores.map((ps) => {
                        return {
                            ...ps.store,
                            // Sets the idVariation in every store because it'll be needed
                            // to check which variation is supposed to have the productStore updated
                            // based on the store. This is ugly, but I blame on the multiselect component.                            
                            idVariation: gridRow.id,
                        }
                    }))
                }

                return {
                    ...gridRow,
                    storeOptions,
                    chosenStore: gridRow.stores.length ? storeOptions[0] : new StoreModel(),
                    chosenProductStore: gridRow.stores.length ? gridRow.stores[0] : new ProductStoreModel(),
                }
            }),
            breadcrumbList: [
                {
                    id: 0,
                    description: 'Produtos',
                    path: '/produtos',
                },
                {
                    id: 1,
                    description: `Detalhes de #${this.$route.params.id}`,
                    path: '',
                },
            ],
       } 
    },
    filters: {
        deliveryDays,
        dateTimeFilter,        
        infoOrPlaceholder,
    },
    mixins: [
        GetAuditUrl,
    ],
    methods: {
        toggleDescription() {
            this.descriptionOpen = !this.descriptionOpen
            saveDescriptionOpen(this.descriptionOpen)
        },
        channelIdOf(storeChannel) {
            return String(storeChannel.idChannel || storeChannel.channel.id)
        },
        // A associação é por loja: a de cada linha, ou a do pai quando só há uma
        storeIdOf(storeChannel) {
            return String(storeChannel.idStore || this.parentStoreId || '')
        },
        checkKey(storeChannel) {
            return `${this.channelIdOf(storeChannel)}@${this.storeIdOf(storeChannel)}`
        },
        onParentChecked(storeChannel, {variationIds}) {
            this.$set(this.parentCheckByChannel, this.checkKey(storeChannel), variationIds)
        },
        // 'ok': a variação está no produto pai associado; 'bad': não está; null: não conferido
        variationMark(storeChannel) {
            const ids = this.parentCheckByChannel[this.checkKey(storeChannel)]

            if (!ids || !storeChannel.idExternalChannel) {
                return null
            }

            return ids.includes(String(storeChannel.idExternalChannel)) ? 'ok' : 'bad'
        },
        isMultiAssociation(storeChannel) {
            const configured = this.channelList.find((c) => String(c.id) === this.channelIdOf(storeChannel))

            return !!configured && !!configured.allowsMultiAssociation && configured.allowsMultiAssociation()
        },
        canAssociateGrid(storeChannel) {
            return !!this.storeIdOf(storeChannel) && !this.isMultiAssociation(storeChannel)
                && getChannelSearchProfile(storeChannel.channel.enumChannel).supportsSearch
        },
        // O vínculo do pai com o canal na loja da linha (pode não existir ainda)
        parentChannelFor(storeChannel) {
            const id = this.channelIdOf(storeChannel)
            const idStore = this.storeIdOf(storeChannel)

            return this.parentChannels.find((sc) => this.channelIdOf(sc) === id && this.storeIdOf(sc) === idStore)
                || {idChannel: id, channel: storeChannel.channel, idExternalChannel: null, idStore}
        },
        // Régua do card do pai: as variações com o id de hoje no canal e na loja do card
        gridCoverageOf(storeChannel) {
            return this.gridVariationsFor(this.channelIdOf(storeChannel), this.storeIdOf(storeChannel))
                .map((v) => ({idMuven: v.idMuven, label: v.values.join(' / '), currentId: v.currentId}))
        },
        storeLabelOf(idStore) {
            const all = [...(this.product.stores || []), ...this.productVariations.flatMap((row) => row.stores || [])]
            const productStore = all.find((ps) => String(ps.idStore) === String(idStore) && ps.store)

            return productStore ? `${productStore.store.idSource} - ${productStore.store.name}` : `Loja ${idStore}`
        },
        // Variações da grade com o id gravado hoje neste canal, na loja informada
        gridVariationsFor(channelId, idStore) {
            return this.productVariations.map((row) => {
                const productStore = (row.stores || []).find((ps) => String(ps.idStore) === String(idStore))
                const storeChannel = productStore && (productStore.channels || []).find((ch) => this.channelIdOf(ch) === channelId)

                return {
                    idMuven: row.id,
                    values: [row.firstVariation, row.secondVariation].filter((v) => !!v),
                    skuCode: row.skuCode,
                    codeSource: row.codeSource,
                    currentId: (storeChannel && storeChannel.idExternalChannel) || null,
                }
            })
        },
        async openGridAssociation(parentStoreChannel, current, focusVariationId = null) {
            const channelId = this.channelIdOf(parentStoreChannel)
            const idStore = this.storeIdOf(parentStoreChannel)
            const result = await this.$refs.gridModal.open({
                storeChannel: parentStoreChannel,
                idStore,
                storeName: this.hasMoreThanOneStore ? this.storeLabelOf(idStore) : null,
                idMuven: this.product.id,
                product: {
                    name: this.product.name,
                    skuCode: this.product.skuCode,
                    codeSource: this.product.codeSource,
                    imageUrl: this.mainImageUrl,
                    axes: this.gridAxes,
                },
                variations: this.gridVariationsFor(channelId, idStore),
                current,
                focusVariationId,
            })

            if (result) {
                this.applyGridAssociation(channelId, idStore, result.entries)
            }
        },
        // A gravação já foi feita; aqui só reflete no produto carregado e nas linhas da grade
        applyGridAssociation(channelId, idStore, entries) {
            const idById = new Map(entries.map((e) => [String(e.idMuven), e.idExternalChannel]))
            const parent = this.parentChannels.find((sc) => this.channelIdOf(sc) === channelId && this.storeIdOf(sc) === idStore)
            const parentChanged = !parent || String(parent.idExternalChannel || '') !== String(idById.get(String(this.product.id)) || '')
            const update = (stores, idMuven) => {
                if (!idById.has(String(idMuven))) {
                    return
                }

                (stores || [])
                    .filter((ps) => String(ps.idStore) === String(idStore))
                    .forEach((ps) => (ps.channels || [])
                        .filter((ch) => this.channelIdOf(ch) === channelId)
                        .forEach((ch) => {
                            ch.idExternalChannel = idById.get(String(idMuven))
                        }))
            }

            update(this.product.stores, this.product.id)
            ;(this.product.children || []).forEach((child) => update(child.stores, child.id))

            this.productVariations.forEach((row) => {
                update(row.stores, row.id)

                if (idById.has(String(row.id))) {
                    (row.uniqueChannels || [])
                        .filter((ch) => this.channelIdOf(ch) === channelId && this.storeIdOf(ch) === idStore)
                        .forEach((ch) => {
                            ch.idExternalChannel = idById.get(String(row.id))
                        })
                }
            })

            // Pai novo: o card confere de novo e manda as variações dele; mesmo pai, a conferência continua valendo
            if (parentChanged) {
                this.$delete(this.parentCheckByChannel, `${channelId}@${idStore}`)
            }
        },
        /*
         * Linhas da tabela de canais da variação, na ordem da listagem de canais:
         * ativos, o resumo dos inativos e, se aberto, os inativos. Só agrupa
         * quando sobra algum ativo à vista.
         */
        variationTableRows(gridRow) {
            const {active, inactive} = splitStoreChannelsByChannelState(gridRow.uniqueChannels || [], this.channelList)
            const toRows = (list, isActive) => list.map((storeChannel, index) => ({
                type: 'channel',
                key: `${isActive ? 'on' : 'off'}-${index}`,
                storeChannel,
                isActive,
            }))

            if (!active.length || !inactive.length) {
                return [...toRows(active, true), ...toRows(inactive, false)]
            }

            return [
                ...toRows(active, true),
                {type: 'group', key: 'group', channels: inactive.map((sc) => sc.channel)},
                ...(this.showInactiveByVariation[gridRow.id] ? toRows(inactive, false) : []),
            ]
        },
        toggleInactiveChannels(gridRow) {
            this.$set(this.showInactiveByVariation, gridRow.id, !this.showInactiveByVariation[gridRow.id])
        },
        toggleVariationCollapse(v) {
            v.collapsed = !v.collapsed
        },
        describeStockConversion,
        openImageViewer(images, index = 0) {
            const list = images || []

            if (!list.length) {
                return
            }

            this.viewer = {images: list, index, visible: true}
        },
        selectProductStore(store) {
            for (const v of this.productVariations) {
                if (v.id === store.idVariation) {
                    v.chosenProductStore = v.stores.find((s) => {
                        return String(s.idStore) === String(store.id) && v.id === store.idVariation
                    }) || new ProductStoreModel()
                }
            }
        },
    },
    computed: {
        ...mapState({
            subscriber: state => state.subscriber,
        }),
        bidimensionalGrid() {
            return this.product.toBidimensionalGrid()
        },
        urlToReservedStockOrder() {
            return (v) => {
                return `/pedidos?custom=true&productIdList=${v.id}&statusList=${OrderStatusEnum.Pending}`
            }
        },
        urlToOrder() {
            return (v) => {
                return `/pedidos?custom=true&productIdList=${v.id}`
            }
        },
        parentChannels() {
            return this.product.getUniqueStoreChannels()
        },
        hasMoreThanOneStore() {
            return this.product.hasMoreThanOneStore()
        },
        // A associação é por loja: com uma só, é a do pai
        parentStoreId() {
            return this.product.hasStores() ? this.product.stores[0].idStore : null
        },
        gridAxes() {
            return [this.bidimensionalGrid.getFirstName(), this.bidimensionalGrid.getSecondName()].filter((n) => !!n).join(' x ')
        },
        // Sem imagem no pai, usa a primeira imagem ativa de alguma variação
        mainImageUrl() {
            const pick = (images) => {
                const list = images || []
                const main = list.find((img) => img.isMain && img.isActive) || list.find((img) => img.isActive)

                return main ? main.url : ''
            }

            return pick(this.product.getImages()) || this.productVariations.map((v) => pick(v.images)).find((url) => !!url) || ''
        },
        importScope() {
            return getImportScope(this.product.allowImport)
        },
        variationsWithConversion() {
            return this.productVariations.filter((v) => hasStockConversion(v.stockConversionRatio))
        },
        // Topo em "baralho": mesma origem do mainImageUrl (pai, ou a primeira variação com imagem ativa)
        heroImages() {
            const parentImages = this.product.getImages()
            const source = hasActiveImage(parentImages)
                ? parentImages
                : this.productVariations.map((v) => v.images).find((images) => hasActiveImage(images))

            return source ? orderImagesForDisplay(source) : []
        },
        heroDeck() {
            return this.heroImages.slice(0, 3)
        },
        descriptionTabs() {
            return [
                {id: 'main', label: 'Principal', has: this.product.hasDescription()},
                {id: 'short', label: 'Curta', has: this.product.hasDescriptionShort()},
                {id: 'long', label: 'Longa', has: this.product.hasDescriptionLong()},
            ]
        },
        currentDescription() {
            const tab = this.descriptionTabs.find((t) => t.id === this.descriptionTab)
            const html = {
                main: () => this.product.getDescriptionWithLineBreaks(),
                short: () => this.product.getDescriptionShortWithLineBreaks(),
                long: () => this.product.getDescriptionLongWithLineBreaks(),
            }

            return {...tab, html: tab.has ? html[tab.id]() : ''}
        },
        collapsedPreview() {
            // Prévia da aba atual; se ela estiver vazia, da primeira que tiver texto
            if (this.currentDescription.has) {
                return descriptionPreview(this.currentDescription.html)
            }

            const html = {
                main: () => this.product.getDescriptionWithLineBreaks(),
                short: () => this.product.getDescriptionShortWithLineBreaks(),
                long: () => this.product.getDescriptionLongWithLineBreaks(),
            }
            const filled = this.descriptionTabs.find((t) => t.has)

            return filled ? descriptionPreview(html[filled.id]()) : ''
        },
        totalStockQty() {
            return this.productVariations.reduce((acc, v) => acc + (v.stockQty || 0), 0)
        },
    },
}
</script>

<style lang="scss" scoped>
@import '@/product/styles/ProductDetail.scss';

.label-with-tooltip {
    display: flex;
    align-items: center;
    gap: 4px;

    label {
        margin-bottom: 0;
    }
}

.pv {
    padding-bottom: 0;
    overflow: hidden;
}

.pv-list {
    margin: 0 -22px;
    border-top: 1px solid var(--border-subtle);
}

.pv-item {
    border-bottom: 1px solid var(--border-subtle);
    transition: background-color .2s;

    &.is-open {
        background: var(--surface-2);

        .pv-chevron {
            transform: rotate(180deg);
        }
    }
}

.pv-row {
    display: grid;
    grid-template-columns: minmax(130px, 200px) minmax(0, 1fr) auto auto 20px;
    align-items: center;
    gap: 10px 16px;
    width: 100%;
    padding: 12px 22px;
    border: 0;
    color: inherit;
    background: transparent;
    text-align: left;
    cursor: pointer;
    transition: background-color .15s;

    &:hover {
        background: var(--row-hover);
    }

    &:focus-visible {
        box-shadow: inset 0 0 0 2px var(--focus-ring);
        outline: none;
    }

    @media (max-width: 767px) {
        grid-template-columns: minmax(0, 1fr) auto 20px;

        .pv-codes,
        .pv-stock {
            grid-column: 1 / -1;
            order: 4;
        }
    }
}

.pv-values {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}

.pv-value {
    padding: 2px 10px;
    border: 1px solid var(--tint-brand-border);
    border-radius: 999px;
    color: var(--brand-strong);
    background: var(--tint-brand-soft);
    font-size: 13px;
    font-weight: 700;
}

.pv-codes {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    min-width: 0;

    span {
        display: inline-flex;
        align-items: baseline;
        gap: 5px;
        min-width: 0;
    }

    small {
        color: var(--text-faint);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: .08em;
        text-transform: uppercase;
    }

    code {
        overflow: hidden;
        color: var(--text-strong);
        font-family: $mono;
        font-size: 13px;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

.pv-stock {
    display: inline-flex;
    align-items: baseline;
    gap: 5px;
    white-space: nowrap;

    strong {
        color: var(--text-strong);
        font-size: 15px;
        font-variant-numeric: tabular-nums;
    }

    small {
        color: var(--text-muted);
        font-size: 12px;
    }
}

.pv-chevron {
    color: var(--text-muted);
    transition: transform .3s $ease-out;
}

.pv-panel {
    padding: 4px 22px 20px;
    animation: ps-rise .35s $ease-out both;

    > .ps-metrics:first-child {
        margin-top: 0;
    }
}

.pv-block {
    margin-top: 18px;
}

.pv-stores {
    padding: 14px;
    border: 1px solid var(--border-subtle);
    border-radius: 12px;
    background: var(--surface-1);

    .ps-metrics {
        margin-bottom: 0;
    }
}

.pv-stores-head {
    display: grid;
    grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
    align-items: end;
    gap: 14px 18px;

    @media (max-width: 767px) {
        grid-template-columns: minmax(0, 1fr);
    }
}

.pv-table-wrap {
    overflow-x: auto;
    border: 1px solid var(--border-subtle);
    border-radius: 12px;
    background: var(--surface-1);
}

.pv-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th {
        padding: 8px 12px;
        border-bottom: 1px solid var(--border-subtle);
        color: var(--text-faint);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: .1em;
        text-align: left;
        text-transform: uppercase;
        white-space: nowrap;
    }

    td {
        padding: 9px 12px;
        border-bottom: 1px solid var(--border-subtle);
        color: var(--text-strong);
        vertical-align: middle;
    }

    tr:last-child td {
        border-bottom: 0;
    }

    .is-num {
        font-variant-numeric: tabular-nums;
        text-align: right;
    }

    .is-nowrap {
        white-space: nowrap;
    }

    .label-with-tooltip {
        display: inline-flex;
    }
}

.pv-table {
    tr.is-off td {
        color: var(--text-muted);
    }

    .pv-table-group td {
        padding: 0;
        background: var(--surface-2);
    }
}

.pv-id-cell {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
}

.pv-mark {
    font-size: 13px;

    &.is-ok { color: var(--success-solid); }
    &.is-bad { color: var(--danger-solid); }
}

.pv-swap {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 8px;
    border: 1px solid var(--border);
    border-radius: 7px;
    color: var(--text-muted);
    background: var(--surface-1);
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    transition: color .15s, background-color .15s, border-color .15s;

    svg {
        font-size: 10px;
    }

    &:hover,
    &:focus-visible {
        color: var(--brand);
        background: var(--tint-brand-bg);
        border-color: var(--tint-brand-border);
        outline: none;
    }
}

.pv-id {
    padding: 2px 8px;
    border-radius: 6px;
    color: var(--text-strong);
    background: var(--surface-3);
    font-family: $mono;
    font-size: 12px;
    font-weight: 600;
    word-break: break-all;
}

.pv-footer {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: 14px;
}

.pv-thumbs {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.pv-thumb {
    width: 64px;
    height: 64px;
    padding: 0;
    overflow: hidden;
    border: 1px solid var(--border-subtle);
    border-radius: 10px;
    background: var(--surface-1);
    cursor: zoom-in;
    transition: transform .2s $ease-out, box-shadow .2s;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    &:hover {
        box-shadow: var(--shadow-md);
        transform: translateY(-2px);
    }
}

.pv-links {
    display: flex;
    gap: 16px;

    a {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        color: var(--brand);
        font-size: 13px;
        font-weight: 700;

        svg {
            font-size: 11px;
        }
    }
}

.pv-count {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    margin: 0 -22px;
    padding: 12px 22px;
    color: var(--text-muted);
    font-size: 13px;
}

.pv-count-separator {
    color: var(--text-disabled);
}
</style>
